<script setup lang="ts">
/**
 * A picture, alone on its own page — or across two, for a spread.
 *
 * The box reaches the full page height directly, rather than the page's body
 * height plus its two margins the way the cover's own full-bleed arrangement
 * does: nothing here needs the on-screen box to line up with anything else,
 * so there is no reason to build the same height from two numbers when one
 * variable already holds it.
 *
 * Only the top and side margins pull negative, never the bottom: a negative
 * bottom margin would drag whatever section comes next up into this one,
 * which is the exact bug the cover's own full-bleed arrangement shipped with
 * once already.
 *
 * **A spread never grows past the ordinary page frame.** An earlier version
 * doubled the box's own width and pulled it out sideways with negative
 * margins, to show the whole picture at its true size while editing. That
 * put a section wider than every other one loose in the canvas, is not
 * reliably restrained by whatever happens to be scrolled into view, and
 * shipped lopsided: bleeding out on one side, an ordinary page's white
 * showing on the other. Staying inside the frame the page already promises
 * every other section is worth more than a life-size preview.
 *
 * So two things are drawn for the same picture, and only one is ever shown:
 *
 * - **The preview**, the whole picture at once, contained rather than
 *   cropped — inevitably letterboxed, since a spread is wider than a page is
 *   tall, but never cut and never past the edge. Canvas only.
 * - **The paginated pair**, the same picture cut into a left and a right
 *   half, each cropped to exactly what its own page would carry — the shape
 *   that matters everywhere a page genuinely is one: print, and any exported
 *   file, since a plain HTML page has no facing page to lay the other half
 *   beside either.
 *
 * Which one paints is a single `display: none`, toggled by whether `.canvas`
 * is an ancestor. Nothing here changes size, position or margin between the
 * two: the failure mode of getting that toggle wrong is "the wrong one of two
 * correctly-drawn things is showing," never a picture spilling out of frame.
 */
import { useI18n } from 'vue-i18n'

import type { ImagePageSection } from '@/domain/section'

const { t } = useI18n()

defineProps<{ section: ImagePageSection }>()
</script>

<template>
  <div v-if="section.spread" class="image-page image-page--spread">
    <template v-if="section.image">
      <img class="image-page__preview" :src="section.image" alt="" />

      <figure class="image-page__half image-page__half--left">
        <img :src="section.image" alt="" />
      </figure>
      <figure class="image-page__half image-page__half--right">
        <img :src="section.image" alt="" />
      </figure>
    </template>
    <div v-else class="image-page__empty">{{ t('canvas.noImage') }}</div>
  </div>

  <figure v-else class="image-page">
    <img v-if="section.image" :src="section.image" alt="" />
    <div v-else class="image-page__empty">{{ t('canvas.noImage') }}</div>
  </figure>
</template>

<style scoped>
/* The whole sheet, edge to edge: pulled out over all four margins. The foot
   is safe to pull too, because the picture always ends its sheet (see
   `domain/sheets.ts`), so nothing after it can be drawn up into the page; left
   in, the bottom margin would spill onto a blank printed page of its own. */
.image-page {
  position: relative;
  height: var(--page-height, 297mm);
  margin: calc(var(--page-margin-top) * -1) calc(var(--page-margin-side, 20mm) * -1)
    calc(var(--page-margin-bottom, 24mm) * -1);
  overflow: hidden;
}

.image-page img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-page__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border: 1px dashed var(--paper-rule);
  color: var(--paper-ink-soft);
  font-family: var(--font-ui);
  font-size: 9pt;
}

/* ---------- Spread: the whole picture, or its paginated halves ---------- */

.image-page--spread {
  /* The paginated pair needs two pages' worth of height between them; the
     base height and clipping above, sized for a single page, would cut the
     second half off entirely rather than let it become its own page. */
  height: auto;
  overflow: visible;
}

.canvas .image-page--spread {
  /* The preview, though, is meant to stay inside one ordinary page's
     footprint, so that constraint is reasserted here. */
  height: var(--page-height, 297mm);
  overflow: hidden;
}

/* The preview: contained, never cropped, never past the frame. Hidden by
   default — shown only inside the canvas, below.
   Qualified with `.image-page--spread` rather than written bare: a bare
   `.image-page__preview` ties with `.image-page img` above on specificity
   (one class each), and loses the tie because that rule also names the `img`
   element — silently falling back to its `object-fit: cover`, which crops
   the preview exactly like a half rather than showing it whole. */
.image-page--spread .image-page__preview {
  display: none;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-page__half {
  position: relative;
  width: 100%;
  height: var(--page-height, 297mm);
  margin: 0;
  overflow: hidden;
}

.image-page__half--left {
  /* Ends the left page. The container's own break-after, in the shared print
     rule below, ends the right one, whatever follows it in the document. */
  break-after: page;
}

/* The picture at twice its box's width, so a box exactly one page wide shows
   exactly one page's worth of a picture drawn at spread size. */
.image-page__half img {
  position: absolute;
  top: 0;
  width: 200%;
  height: 100%;
  object-fit: cover;
}

.image-page__half--left img {
  left: 0;
}

.image-page__half--right img {
  left: -100%;
}

/* Only inside the live editing canvas: the preview instead of the paginated
   halves, since the canvas is one continuous sheet with no facing page to
   fold the picture onto, and seeing it whole is the more useful preview
   while placing it. A print, and an exported file alike, never carry
   `.canvas` as an ancestor, so both keep the paginated shape above instead —
   this is the only difference between them, nothing about size or position. */
.canvas .image-page__preview {
  display: block;
}

.canvas .image-page__half {
  display: none;
}

@media print {
  .image-page {
    /* Always alone on its page, whatever comes before or after it: that is
       the one thing this kind promises over a page break's own backdrop. */
    break-before: page;
    break-after: page;
  }

  .image-page__empty {
    /* An empty insert has nothing to print; an empty dashed box would. */
    display: none;
  }
}
</style>
